import { useParams } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { SiteLayout } from '@/components/site'
import { LoadingOverlay } from '@/components/ui'
import { getPage } from '@/lib/api'
import type { PageData } from '@/types'

export function DynamicPage() {
  const { slug } = useParams<{ slug: string }>()
  const [page, setPage] = useState<PageData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!slug) return

    const fetchPage = async () => {
      setIsLoading(true)
      setError(null)

      try {
        const data = await getPage(slug)
        setPage(data)

        // Update document title
        document.title = `${data.title} | CloudCMS`

        // Update meta description if available
        if (data.meta?.description) {
          let metaDescription = document.querySelector('meta[name="description"]')
          if (!metaDescription) {
            metaDescription = document.createElement('meta')
            metaDescription.setAttribute('name', 'description')
            document.head.appendChild(metaDescription)
          }
          metaDescription.setAttribute('content', data.meta.description)
        }
      } catch {
        setError('Page non trouvee')
      } finally {
        setIsLoading(false)
      }
    }

    fetchPage()
  }, [slug])

  if (isLoading) {
    return (
      <SiteLayout>
        <div className="py-24">
          <LoadingOverlay message="Chargement..." />
        </div>
      </SiteLayout>
    )
  }

  if (error || !page) {
    return (
      <SiteLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
          <h1 className="text-4xl font-bold text-gray-900">404</h1>
          <p className="mt-4 text-lg text-gray-600">
            {error || 'Cette page n\'existe pas'}
          </p>
        </div>
      </SiteLayout>
    )
  }

  return (
    <SiteLayout>
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
          {page.title}
        </h1>

        <div
          className="mt-8 prose prose-lg max-w-none"
          dangerouslySetInnerHTML={{ __html: page.content }}
        />
      </article>
    </SiteLayout>
  )
}
